import { useEffect, useState } from 'react'
import { Check, ImageIcon } from 'lucide-react'
import { Button, Modal, Text } from '../../ui'
import { cn } from '../../../lib/cn'
import {
  fetchHeaderImagePhotos,
  type HeaderImage,
  type HeaderImageCategory,
} from '../../../lib/unsplash-api'

const CATEGORIES: { id: HeaderImageCategory; label: string }[] = [
  { id: 'minimal', label: 'Minimal' },
  { id: 'nature', label: 'Nature' },
  { id: 'more', label: 'More' },
]

type HeaderImagePickerProps = {
  open: boolean
  onClose: () => void
  workspaceId: string
  value: HeaderImage | null
  onChange: (image: HeaderImage | null) => void
}

export function HeaderImagePicker({
  open,
  onClose,
  workspaceId,
  value,
  onChange,
}: HeaderImagePickerProps) {
  const [category, setCategory] = useState<HeaderImageCategory>('minimal')
  const [photos, setPhotos] = useState<HeaderImage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    let cancelled = false

    async function loadPhotos() {
      setLoading(true)
      setError(null)

      const result = await fetchHeaderImagePhotos(workspaceId, category)

      if (cancelled) return

      if (!result.success) {
        setPhotos([])
        setError(result.error.message)
      } else {
        setPhotos(result.data)
      }

      setLoading(false)
    }

    void loadPhotos()

    return () => {
      cancelled = true
    }
  }, [open, workspaceId, category])

  function handleSelect(photo: HeaderImage) {
    onChange(photo)
    onClose()
  }

  function handleRemove() {
    onChange(null)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} className="sm:max-w-2xl">
      <div className="flex flex-col gap-4">
        <div>
          <Text variant="h2">Choose hero background</Text>
          <Text variant="caption" className="mt-1 text-foreground/45">
            A wide background image for the hero section on your public knowledge base
            home page — not a profile or cover photo.
          </Text>
        </div>

        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setCategory(item.id)}
              className={cn(
                'rounded-full px-3 py-1.5 text-sm transition-colors',
                category === item.id
                  ? 'bg-foreground text-white'
                  : 'bg-stone text-foreground/60 hover:text-foreground',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex min-h-48 items-center justify-center">
            <Text variant="body-sm" className="text-foreground/45">
              Loading photos…
            </Text>
          </div>
        ) : error ? (
          <div className="flex min-h-48 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-foreground/15 px-4 py-8 text-center">
            <ImageIcon className="h-8 w-8 text-foreground/25" />
            <Text variant="body-sm" className="text-foreground/60">
              {error}
            </Text>
          </div>
        ) : photos.length === 0 ? (
          <div className="flex min-h-48 items-center justify-center">
            <Text variant="body-sm" className="text-foreground/45">
              No photos found for this category.
            </Text>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {photos.map((photo) => {
              const selected = value?.id === photo.id

              return (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => handleSelect(photo)}
                  aria-label={`Select photo by ${photo.photographerName}`}
                  className={cn(
                    'group relative aspect-[21/9] overflow-hidden rounded-xl border-2 transition-colors',
                    selected
                      ? 'border-foreground'
                      : 'border-transparent hover:border-foreground/20',
                  )}
                >
                  <img
                    src={photo.thumbUrl}
                    alt={photo.alt}
                    className="h-full w-full object-cover"
                  />
                  <div
                    className={cn(
                      'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-6 pb-2',
                      'opacity-0 transition-opacity group-hover:opacity-100',
                    )}
                  >
                    <span className="block truncate text-left text-xs text-white">
                      {photo.photographerName}
                    </span>
                  </div>
                  {selected && (
                    <span className="absolute inset-0 flex items-center justify-center bg-foreground/30">
                      <Check className="h-6 w-6 text-white" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-foreground/10 pt-4">
          <Text variant="caption" className="text-foreground/45">
            Photos from{' '}
            <a
              href="https://unsplash.com/?utm_source=sondersoft&utm_medium=referral"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Unsplash
            </a>
          </Text>
          {value && (
            <Button variant="outline" size="sm" onClick={handleRemove}>
              Remove background
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
